import React, { useState } from 'react';
import { Search, Filter, SortAsc, SortDesc, X, ChevronDown } from 'lucide-react';

const SearchAndFilters = ({ 
  searchTerm, 
  setSearchTerm, 
  sortBy, 
  setSortBy, 
  sortOrder, 
  setSortOrder,
  priceRange,
  setPriceRange,
  kmsRange,
  setKmsRange,
  showFilters,
  setShowFilters,
  selectedQuery,
  setSelectedQuery,
  queries,
  yearRange,
  setYearRange,
  years
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFromYearDropdownOpen, setIsFromYearDropdownOpen] = useState(false);
  const [isToYearDropdownOpen, setIsToYearDropdownOpen] = useState(false);
  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('price_change');
    setSortOrder('desc');
    setPriceRange([0, 50000]);
    setKmsRange([0, 200000]);
    setSelectedQuery('');
    const minYear = years.length > 0 ? Math.min(...years) : 1990;
    const maxYear = new Date().getFullYear();
    setYearRange([minYear, maxYear]);
  };

  return (
    <div className="mb-8">
      {/* Barra de búsqueda principal */}
      <div className="flex flex-col gap-3 lg:gap-4 mb-4">
        {/* Primera fila: Query selector + Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Selector de Query - Dropdown personalizado */}
          <div className="sm:w-60 lg:w-80 relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full px-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white font-medium uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-dark-text focus:border-transparent hover:border-gray-600 transition-all duration-200 flex items-center justify-between"
          >
            <span className={selectedQuery ? 'text-white' : 'text-gray-400 normal-case'}>
              {selectedQuery ? selectedQuery.replace('-', ' ') : 'Seleccionar categoría...'}
            </span>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Dropdown menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#1c1c1a] border border-gray-700 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
              <button
                onClick={() => {
                  setSelectedQuery('');
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-3 text-left text-gray-400 hover:bg-[#232321] hover:text-white transition-colors duration-200 normal-case"
              >
                Seleccionar categoría...
              </button>
              {queries.map((query) => (
                <button
                  key={query}
                  onClick={() => {
                    setSelectedQuery(query);
                    setIsDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left text-white uppercase hover:bg-[#232321] transition-colors duration-200"
                >
                  {query.replace('-', ' ')}
                </button>
              ))}
            </div>
          )}
          
          {/* Overlay para cerrar dropdown */}
          {isDropdownOpen && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsDropdownOpen(false)}
            />
          )}
        </div>

        {/* Campo de búsqueda */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por título, marca o modelo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dark-text focus:border-transparent transition-all duration-200"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        </div>

        {/* Ordenamiento */}
        <div className="flex gap-2 w-full sm:w-auto">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 sm:flex-none px-3 lg:px-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-dark-text focus:border-transparent"
          >
            <option value="title">Título</option>
            <option value="price">Precio</option>
            <option value="kilometers">Kms</option>
            <option value="price_change">Variación</option>
            <option value="last_price_date">Últ. cambio precio</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 lg:px-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white hover:bg-[#232321] transition-colors"
          >
            {sortOrder === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
          </button>

          {/* Botón de filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 lg:px-4 py-3 rounded-xl border transition-all duration-200 flex items-center space-x-1 lg:space-x-2 ${
              showFilters 
                ? 'bg-dark-text text-white border-dark-text' 
                : 'bg-[#1c1c1a] text-gray-300 border-gray-700 hover:bg-[#232321]'
            }`}
          >
            <Filter className="w-5 h-5" />
            <span className="hidden sm:inline">Filtros</span>
          </button>
        </div>
      </div>

      {/* Panel de filtros expandible */}
      {showFilters && (
        <div className="glass-effect rounded-xl p-6 animate-slide-up relative z-30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Filtros Avanzados</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Limpiar filtros
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-4">
            {/* Filtro de precio */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Rango de Precio: ${priceRange[0].toLocaleString()} - ${priceRange[1].toLocaleString()}
              </label>
              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="50000"
                  step="1000"
                  value={priceRange[0]}
                  onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <input
                  type="range"
                  min="0"
                  max="50000"
                  step="1000"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            </div>

            {/* Filtro de kilómetros */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Rango de Kilómetros: {kmsRange[0].toLocaleString()} - {kmsRange[1].toLocaleString()} km
              </label>
              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="5000"
                  value={kmsRange[0]}
                  onChange={(e) => setKmsRange([parseInt(e.target.value), kmsRange[1]])}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="5000"
                  value={kmsRange[1]}
                  onChange={(e) => setKmsRange([kmsRange[0], parseInt(e.target.value)])}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            </div>

            {/* Filtro de año */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Rango de Años
              </label>
              <div className="grid grid-cols-2 gap-2">
                {/* Dropdown "Desde" */}
                <div className="relative">
                  <label className="block text-xs text-gray-400 mb-1">Desde</label>
                  <button
                    onClick={() => setIsFromYearDropdownOpen(!isFromYearDropdownOpen)}
                    className="w-full px-3 py-2 bg-[#1c1c1a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-dark-text focus:border-transparent hover:border-gray-600 transition-all duration-200 flex items-center justify-between"
                  >
                    <span>{yearRange[0]}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isFromYearDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isFromYearDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsFromYearDropdownOpen(false)}
                      />
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#1c1c1a] border border-gray-700 rounded-lg shadow-2xl z-50 max-h-40 overflow-y-auto">
                        {years.filter(year => year <= yearRange[1]).sort((a, b) => b - a).map((year) => (
                          <button
                            key={year}
                            onClick={() => {
                              setYearRange([year, yearRange[1]]);
                              setIsFromYearDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-[#232321] transition-colors duration-200 ${
                              yearRange[0] === year ? 'bg-[#232321] text-[#7b8fa8]' : 'text-white'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Dropdown "Hasta" */}
                <div className="relative">
                  <label className="block text-xs text-gray-400 mb-1">Hasta</label>
                  <button
                    onClick={() => setIsToYearDropdownOpen(!isToYearDropdownOpen)}
                    className="w-full px-3 py-2 bg-[#1c1c1a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-dark-text focus:border-transparent hover:border-gray-600 transition-all duration-200 flex items-center justify-between"
                  >
                    <span>{yearRange[1]}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isToYearDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isToYearDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsToYearDropdownOpen(false)}
                      />
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#1c1c1a] border border-gray-700 rounded-lg shadow-2xl z-50 max-h-40 overflow-y-auto">
                        {years.filter(year => year >= yearRange[0]).sort((a, b) => b - a).map((year) => (
                          <button
                            key={year}
                            onClick={() => {
                              setYearRange([yearRange[0], year]);
                              setIsToYearDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-[#232321] transition-colors duration-200 ${
                              yearRange[1] === year ? 'bg-[#232321] text-[#7b8fa8]' : 'text-white'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAndFilters;